import { useState, useEffect, useRef } from "react";

//EX 18-09-2026 (unificacion alta/edicion, fase 2 - paso 1): el nomenclador de partido /
//circunscripcion / seccion estaba implementado DOS veces y de forma distinta:
//  - el alta pedia las circ al cambiar el partido, y las secc al elegir una circ (correcto);
//  - la edicion pedia las circ UNA sola vez al montar (por eso cambiar el partido NO refrescaba las
//    circunscripciones y se podia guardar una combinacion partido/circ inconsistente), pedia las secc
//    tambien al montar con circ vacio (traia la lista completa sin usarla), y arrastraba funciones
//    muertas (getCircEdit, loadCIRC, recorrerCirc).
//Se unifica aca con el comportamiento correcto. De paso implementa el punto 7.8 del plan de
//optimizacion: la lista de secciones se pide recien cuando hay una circunscripcion elegida.
//
//El hook NO toca los inputs del formulario: solo mantiene las LISTAS de opciones. Cuando el usuario
//cambia el partido avisa por onPartidoCambia para que el formulario limpie la circ/secc elegidas (eso
//es lo que hacia el getCirc del alta, y es lo que evita guardar una combinacion inconsistente).
//
//EX 18-09-2026 (decision del 18/09): en la EDICION el partido ahora queda bloqueado (ver datosEditExp),
//porque la nomenclatura depende de el. Asi que el cambio de partido solo puede ocurrir en el alta, y
//onPartidoCambia es la red por si alguna vez se desbloquea.
//
//Importante: onPartidoCambia NO se llama en el primer render. En edicion los valores vienen
//precargados de la base, y limpiarlos al montar los borraria.
const useNomenclador = ({ partido, circ, onPartidoCambia }) => {
	const [circS, setCirc] = useState([]);
	const [seccS, setSecc] = useState([]);

	const optionsNull = [{ value: "", label: "" }];
	const partidoPrevio = useRef(undefined);

	//Mismo pedido que hacian las dos versiones (headers incluidos).
	const traer = async (url) => {
		const headers = { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*', jwt_token: localStorage.token };
		const res = await fetch(url, { headers });
		return res.json();
	};

	//Circunscripciones del partido: al montar y cada vez que cambia el partido.
	useEffect(() => {
		const partidoAnterior = partidoPrevio.current;
		partidoPrevio.current = partido;

		//Solo cuando el partido CAMBIA respecto de un valor ya establecido: no en el montaje, ni al pasar
		//de vacio al valor precargado (en edicion el partido llega ya cargado). Limpiar ahi borraria los
		//valores que vienen de la base.
		if (partidoAnterior && partidoAnterior !== partido) {
			if (onPartidoCambia) onPartidoCambia();
			setSecc([]); // la lista de secciones era la del partido anterior
		}

		if (!partido) {
			setCirc([]);
			setSecc([]);
			return;
		}

		//OJO: este .then NO debe tocar la lista de secciones. Antes hacia setSecc([]), y como al montar
		//los pedidos de circ y de secc salen EN PARALELO, si este resolvia despues borraba la lista que
		//el otro ya habia cargado: el select de Secc quedaba sin opciones aunque el endpoint devolviera
		//datos. Comprobado el 19-09-2026 con el expediente 18296 (partido 3, circ 01): el endpoint
		//devolvia 15 secciones (0A..0L), el front habia hecho el pedido, y la pantalla no mostraba
		//ninguna. El vaciado de la lista ahora ocurre solo cuando cambia el partido, arriba.
		traer(`/api/api_serv/nomencla/circ?partido=${partido}`)
			.then(res => setCirc([...optionsNull, ...res]))
			.catch(err => console.log(err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [partido]);

	//Secciones: solo con una circunscripcion elegida. Con circ vacio no se pide nada (antes se pedia
	//al montar y el resultado no se usaba).
	//
	//OJO CON LAS DEPENDENCIAS: solo [circ], NO [partido, circ]. Los dos efectos corren en el MISMO commit,
	//asi que si este dependiera tambien de partido, al cambiar de partido se dispararia con la circ
	//VIEJA (la limpieza que pide onPartidoCambia recien se aplica en el render siguiente): salia un
	//pedido inutil con el partido nuevo y la circ del partido anterior. Comprobado en los logs del
	//servidor (18-09-2026): "partido: '53', circ: '02'" al cambiar del partido 15 (que es el de la circ
	//02) al 53. Con la dependencia en [circ] ese pedido no sale: el cambio de partido limpia la circ y
	//este efecto corre con circ vacio, que solo vacia la lista.
	useEffect(() => {
		if (!partido || !circ) {
			setSecc([]);
			return;
		}

		traer(`/api/api_serv/nomencla/secc?partido=${partido}&circ=${circ}`)
			.then(res => setSecc([...optionsNull, ...res]))
			.catch(err => console.log(err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [circ]);

	return { circS, seccS };
};

export default useNomenclador;
